import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert, AlertIcon, Badge, Box, Button, HStack, Input, Select, Spinner, Switch, Table, Tbody, Td, Text, Th, Thead,
  Tr, VStack,
} from "@chakra-ui/react";
import { loadCurrentStaffUser } from "../../../utils/currentStaffUser";

/**
 * Fifth tab of the Live Activity modal: who gets the Live Activity emails, and what the alerting is
 * doing. Each person on the list (public.alert_recipients) picks a time zone and an hour: the
 * morning summary of the three issue tabs reaches them at that hour where they are, if they're on
 * the morning list. Urgent alerts (the database down or getting worse, a server near its memory
 * limit, a serious problem in the night's AI review) go to everyone on the urgent list.
 *
 * The emails are sent by the one backend where NIGHTLY_CHECKS is true (backend/api/alerts/), which
 * records each one in public.alert_log: the table at the bottom. Every change saves at once.
 */

export const DEFAULT_TZ = "America/New_York";
export const DEFAULT_HOUR = 8;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const HOURS = Array.from({ length: 24 }, (_, h) => h);

const ZONE_NAMES = {
  "America/Los_Angeles": "Los Angeles (Pacific)",
  "America/Denver": "Denver (Mountain)",
  "America/Phoenix": "Phoenix (Arizona)",
  "America/Chicago": "Chicago (Central)",
  "America/New_York": "New York (Eastern)",
  "America/Anchorage": "Anchorage (Alaska)",
  "Pacific/Honolulu": "Honolulu (Hawaii)",
  "America/Puerto_Rico": "Puerto Rico (Atlantic)",
  "America/Toronto": "Toronto (Eastern)",
  "America/Mexico_City": "Mexico City",
  "Europe/London": "London (UK)",
  "Europe/Paris": "Paris (Central Europe)",
  "Europe/Berlin": "Berlin (Central Europe)",
  "Asia/Dubai": "Dubai (Gulf)",
  "Asia/Kolkata": "India",
  "Asia/Manila": "Manila (Philippines)",
  "Asia/Tokyo": "Tokyo (Japan)",
  "Australia/Sydney": "Sydney (Australia)",
};

/** "Los Angeles (Pacific)" for America/Los_Angeles; the city for any other IANA zone. */
export function zoneLabel(zone) {
  if (ZONE_NAMES[zone]) return ZONE_NAMES[zone];
  const city = String(zone || "").split("/").pop().replace(/_/g, " ");
  return city || String(zone || "");
}

/** 0 → "12 AM", 8 → "8 AM", 13 → "1 PM". */
export function hourLabel(hour) {
  const h = Number(hour);
  return `${h % 12 === 0 ? 12 : h % 12} ${h < 12 ? "AM" : "PM"}`;
}

/** A moment in the viewer's own time zone. */
export function formatWhen(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export const KIND_WORDS = {
  morning: "Morning summary",
  db_down: "Database down",
  db_worse: "Database getting worse",
  db_still_down: "Database still down",
  db_recovered: "Database recovered",
  memory: "Server near its memory limit",
  code: "Serious problem in the code",
  test: "Test email",
};

const SEND_STATUS = {
  sent: { label: "SENT", scheme: "green" },
  failed: { label: "FAILED", scheme: "red" },
  sending: { label: "SENDING", scheme: "gray" },
};

const WATCHER_STATE = {
  ok: { label: "HEALTHY", scheme: "green" },
  warning: { label: "KEEP AN EYE ON", scheme: "orange" },
  problem: { label: "PROBLEM", scheme: "red" },
  unknown: { label: "COULDN'T CHECK", scheme: "gray" },
};

function serverWords(server) {
  const text = String(server || "");
  const at = text.indexOf(":");
  const kind = at >= 0 ? text.slice(0, at) : "";
  const name = at >= 0 ? text.slice(at + 1) : text;
  if (kind === "render") return `the Render service ${name}`;
  if (kind === "local") return name ? `the computer ${name}` : "a local computer";
  return name || "this backend";
}

function people(n, verb) {
  return `${n} ${n === 1 ? `person ${verb}s` : `people ${verb}`}`;
}

function messageOf(e) {
  return e?.message || String(e);
}

function ZoneSelect({ value, zones, onChange, label, testId, isDisabled }) {
  // A zone saved some other way still shows, even when it isn't one of the common ones.
  const options = !value || zones.includes(value) ? zones : [value, ...zones];
  return (
    <Select size="sm" minW="230px" value={value} onChange={(e) => onChange(e.target.value)} aria-label={label}
            data-testid={testId} isDisabled={isDisabled}>
      {options.map((zone) => {
        const name = zoneLabel(zone);
        return <option key={zone} value={zone}>{name === zone ? zone : `${name} · ${zone}`}</option>;
      })}
    </Select>
  );
}

function HourSelect({ value, onChange, label, testId, isDisabled }) {
  return (
    <Select size="sm" w="100px" value={String(value)} onChange={(e) => onChange(Number(e.target.value))}
            aria-label={label} data-testid={testId} isDisabled={isDisabled}>
      {HOURS.map((h) => <option key={h} value={String(h)}>{hourLabel(h)}</option>)}
    </Select>
  );
}

function RecipientRow({ recipient, zones, save, remove, onChanged }) {
  const [draft, setDraft] = useState(recipient);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const { email } = recipient;
  useEffect(() => { setDraft(recipient); }, [recipient]);

  const change = (field, value) => {
    const next = { ...draft, [field]: value };
    setDraft(next);
    setBusy(true);
    setError(null);
    Promise.resolve()
      .then(() => save(next))
      .then(() => onChanged())
      .catch((e) => {
        setDraft(recipient);
        setError(`Couldn't save the change for ${email}: ${messageOf(e)}`);
      })
      .finally(() => setBusy(false));
  };

  const removeNow = () => {
    setBusy(true);
    setError(null);
    Promise.resolve()
      .then(() => remove(recipient.id))
      .then(() => onChanged())
      .catch((e) => {
        setConfirming(false);
        setError(`Couldn't remove ${email}: ${messageOf(e)}`);
      })
      .finally(() => setBusy(false));
  };

  const hour = draft.morning_hour ?? DEFAULT_HOUR;
  return (
    <>
      <Tr data-testid={`alerts-row-${email}`}>
        <Td px={2}><Text fontSize="sm" fontWeight="semibold" wordBreak="break-all">{email}</Text></Td>
        <Td px={2}>
          <ZoneSelect value={draft.timezone || DEFAULT_TZ} zones={zones} onChange={(z) => change("timezone", z)}
                      label={`Time zone for ${email}`} testId={`alerts-tz-${email}`} isDisabled={busy} />
        </Td>
        <Td px={2}>
          <HourSelect value={hour} onChange={(h) => change("morning_hour", h)} label={`Morning hour for ${email}`}
                      testId={`alerts-hour-${email}`} isDisabled={busy} />
        </Td>
        <Td px={2}>
          <Switch isChecked={draft.morning !== false} onChange={(e) => change("morning", e.target.checked)}
                  aria-label={`Morning summary for ${email}`} isDisabled={busy} colorScheme="purple" />
        </Td>
        <Td px={2}>
          <Switch isChecked={draft.urgent !== false} onChange={(e) => change("urgent", e.target.checked)}
                  aria-label={`Urgent alerts for ${email}`} isDisabled={busy} colorScheme="red" />
        </Td>
        <Td px={2} textAlign="right">
          {confirming ? (
            <HStack spacing={1} justify="flex-end">
              <Text fontSize="xs" color="gray.600">Remove?</Text>
              <Button size="xs" colorScheme="red" onClick={removeNow} isLoading={busy}
                      data-testid={`alerts-remove-confirm-${email}`}>
                Yes, remove
              </Button>
              <Button size="xs" variant="ghost" onClick={() => setConfirming(false)} isDisabled={busy}>Cancel</Button>
            </HStack>
          ) : (
            <Button size="xs" variant="outline" colorScheme="red" onClick={() => setConfirming(true)}
                    isDisabled={busy} data-testid={`alerts-remove-${email}`}>
              Remove
            </Button>
          )}
        </Td>
      </Tr>
      {error && (
        <Tr>
          <Td colSpan={6} px={2} pt={0} borderTopWidth={0}>
            <Text fontSize="xs" color="red.600" data-testid={`alerts-row-error-${email}`}>{error}</Text>
          </Td>
        </Tr>
      )}
    </>
  );
}

function AddRecipient({ zones, existing, save, onAdded }) {
  const [email, setEmail] = useState("");
  const [timezone, setTimezone] = useState(DEFAULT_TZ);
  const [hour, setHour] = useState(DEFAULT_HOUR);
  const [morning, setMorning] = useState(true);
  const [urgent, setUrgent] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const add = () => {
    const address = email.trim().toLowerCase();
    if (!EMAIL.test(address)) {
      setError("Enter a valid email address.");
      return;
    }
    if (existing.includes(address)) {
      setError(`${address} is already on the list: change its settings above.`);
      return;
    }
    setSaving(true);
    setError(null);
    Promise.resolve()
      .then(() => save({ email: address, timezone, morning_hour: hour, morning, urgent }))
      .then(() => {
        setEmail("");
        return onAdded();
      })
      .catch((e) => setError(`Couldn't add ${address}: ${messageOf(e)}`))
      .finally(() => setSaving(false));
  };

  return (
    <Box mt={3} pt={3} borderTopWidth="1px" data-testid="alerts-add">
      <Text fontSize="sm" fontWeight="semibold" mb={2}>Add someone</Text>
      <HStack spacing={3} flexWrap="wrap" align="center">
        <Input size="sm" type="email" maxW="260px" placeholder="name@example.com" value={email}
               onChange={(e) => { setEmail(e.target.value); setError(null); }}
               onKeyDown={(e) => { if (e.key === "Enter") add(); }}
               aria-label="Email to add" data-testid="alerts-add-email" />
        <ZoneSelect value={timezone} zones={zones} onChange={setTimezone} label="Time zone for the new person"
                    testId="alerts-add-tz" />
        <HourSelect value={hour} onChange={setHour} label="Morning hour for the new person" testId="alerts-add-hour" />
        <HStack spacing={2}>
          <Switch isChecked={morning} onChange={(e) => setMorning(e.target.checked)} colorScheme="purple"
                  aria-label="Morning summary for the new person" />
          <Text fontSize="sm">Morning summary</Text>
        </HStack>
        <HStack spacing={2}>
          <Switch isChecked={urgent} onChange={(e) => setUrgent(e.target.checked)} colorScheme="red"
                  aria-label="Urgent alerts for the new person" />
          <Text fontSize="sm">Urgent alerts</Text>
        </HStack>
        <Button size="sm" colorScheme="blue" onClick={add} isLoading={saving} data-testid="alerts-add-save">Add</Button>
      </HStack>
      {error && <Text fontSize="xs" color="red.600" mt={1} data-testid="alerts-add-error">{error}</Text>}
    </Box>
  );
}

function TestEmail({ sendTest, loadMe }) {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);      // {ok, text}
  const touched = useRef(false);

  // Prefilled with the signed-in person's address, unless they've already typed one.
  useEffect(() => {
    if (!loadMe) return undefined;
    let cancelled = false;
    Promise.resolve()
      .then(() => loadMe())
      .then((me) => { if (!cancelled && me?.email && !touched.current) setEmail(me.email); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [loadMe]);

  const send = () => {
    const address = email.trim();
    if (!EMAIL.test(address)) {
      setResult({ ok: false, text: "Enter a valid email address." });
      return;
    }
    setSending(true);
    setResult(null);
    Promise.resolve()
      .then(() => sendTest(address))
      .then((res) => setResult({
        ok: true,
        text: `Sent a test email to ${res?.email || address}. It should arrive within a minute: check the spam folder if it doesn't.`,
      }))
      .catch((e) => setResult({ ok: false, text: `Couldn't send it: ${messageOf(e)}` }))
      .finally(() => setSending(false));
  };

  return (
    <Box borderWidth="1px" borderRadius="md" p={3} data-testid="alerts-test">
      <Text fontWeight="bold" mb={1}>Send me a test email</Text>
      <Text fontSize="sm" color="gray.600" mb={2}>
        Checks that email reaches an address before anyone relies on it. It goes out the same way the alerts do.
      </Text>
      <HStack spacing={3} flexWrap="wrap">
        <Input size="sm" type="email" maxW="300px" placeholder="name@example.com" value={email}
               onChange={(e) => { touched.current = true; setEmail(e.target.value); setResult(null); }}
               aria-label="Email for the test" data-testid="alerts-test-email" />
        <Button size="sm" variant="outline" colorScheme="blue" onClick={send} isLoading={sending}
                loadingText="Sending…" data-testid="alerts-test-send">
          Send test email
        </Button>
      </HStack>
      {result && (
        <Text fontSize="sm" mt={2} color={result.ok ? "green.700" : "red.600"} data-testid="alerts-test-result">
          {result.text}
        </Text>
      )}
    </Box>
  );
}

function WhoGetsThem({ recipients, fallback }) {
  if (!recipients.length) {
    return fallback.length ? (
      <Text data-testid="alerts-who">
        Nobody is on the list, so urgent alerts go to {fallback.join(", ")} (DB_HEALTH_ALERT_EMAILS).
      </Text>
    ) : (
      <Text data-testid="alerts-who" color="orange.700">Nobody gets alerts yet.</Text>
    );
  }
  const morning = recipients.filter((r) => r.morning !== false).length;
  const urgent = recipients.filter((r) => r.urgent !== false).length;
  return (
    <Text data-testid="alerts-who">
      {people(morning, "get")} the morning summary · {people(urgent, "get")} urgent alerts.
    </Text>
  );
}

function Watcher({ watcher }) {
  const w = watcher || {};
  const state = WATCHER_STATE[w.overall];
  return (
    <Box data-testid="alerts-watcher">
      {w.running ? (
        <HStack spacing={2} flexWrap="wrap">
          <Text>
            Database watcher: checks every {w.every_s || 60} s
            {w.checked_at ? `, last at ${formatWhen(w.checked_at)}.` : ", no check yet."}
          </Text>
          {state && <Badge colorScheme={state.scheme}>{state.label}</Badge>}
          {w.alerting && <Badge colorScheme="red" variant="solid">ALERTING</Badge>}
        </HStack>
      ) : (
        <Text color="gray.600">Database watcher: not running here.{w.reason ? ` ${w.reason}` : ""}</Text>
      )}
      {w.problems?.length > 0 && (
        <Text color="red.600" data-testid="alerts-watcher-problems">Problems now: {w.problems.join(", ")}</Text>
      )}
      {w.last_email && (
        <Text color="gray.700">Last alert email: {formatWhen(w.last_email.at)}, “{w.last_email.subject}”</Text>
      )}
      {w.last_error && (
        <Text color="orange.700" data-testid="alerts-watcher-error">
          {formatWhen(w.last_error.at)}: {w.last_error.message}
        </Text>
      )}
    </Box>
  );
}

function StatusBox({ data }) {
  return (
    <Box borderWidth="1px" borderRadius="md" p={3} data-testid="alerts-status">
      <Text fontWeight="bold" mb={2}>What the alerting is doing</Text>
      <VStack align="stretch" spacing={1.5} fontSize="sm">
        <HStack spacing={2} align="baseline">
          <Badge colorScheme={data.alerts_here ? "green" : "gray"}>{data.alerts_here ? "ON HERE" : "NOT HERE"}</Badge>
          <Text data-testid="alerts-here">
            {data.alerts_here
              ? `Alerts run on this server (${serverWords(data.server)}).`
              : "Alerts don't run on this server. They run where NIGHTLY_CHECKS is true."}
          </Text>
        </HStack>
        <WhoGetsThem recipients={data.recipients || []} fallback={data.fallback || []} />
        <Watcher watcher={data.watcher} />
      </VStack>
    </Box>
  );
}

function RecentEmails({ recent }) {
  return (
    <Box borderWidth="1px" borderRadius="md" p={3} data-testid="alerts-recent">
      <Text fontWeight="bold" mb={2}>Recent emails</Text>
      {recent.length === 0 ? (
        <Text fontSize="sm" color="gray.600">No emails have been sent yet.</Text>
      ) : (
        <Box overflowX="auto">
          <Table size="sm" variant="simple">
            <Thead>
              <Tr>
                <Th px={2}>When</Th><Th px={2}>What</Th><Th px={2}>To</Th><Th px={2}>Subject</Th><Th px={2}>Status</Th>
              </Tr>
            </Thead>
            <Tbody>
              {recent.map((r, i) => {
                const status = SEND_STATUS[r.status] || SEND_STATUS.sending;
                return (
                  <Tr key={r.id ?? i} data-testid={`alerts-recent-${r.id ?? i}`}>
                    <Td px={2} whiteSpace="nowrap" fontSize="xs">{formatWhen(r.sent_at || r.created_at)}</Td>
                    <Td px={2} fontSize="xs">{KIND_WORDS[r.kind] || r.kind}</Td>
                    <Td px={2} fontSize="xs" wordBreak="break-all">{r.recipient}</Td>
                    <Td px={2} fontSize="xs">
                      {r.subject}
                      {r.status === "failed" && r.error && <Text color="red.600" mt={0.5}>{r.error}</Text>}
                    </Td>
                    <Td px={2}><Badge colorScheme={status.scheme} title={r.error || undefined}>{status.label}</Badge></Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      )}
    </Box>
  );
}

export default function AlertsPanel({ load, save, remove, sendTest, loadMe = loadCurrentStaffUser }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const reload = useCallback(() => {
    setLoading(true);
    setError(null);
    return Promise.resolve()
      .then(() => load())
      .then((result) => { if (mounted.current) setData(result); })
      .catch((e) => { if (mounted.current) setError(messageOf(e)); })
      .finally(() => { if (mounted.current) setLoading(false); });
  }, [load]);

  useEffect(() => { reload(); }, [reload]);

  const recipients = data?.recipients || [];
  const zones = data?.timezones?.length ? data.timezones : [DEFAULT_TZ];
  const existing = recipients.map((r) => String(r.email || "").toLowerCase());

  return (
    <VStack align="stretch" spacing={4}>
      <Alert status="info" borderRadius="md">
        <AlertIcon />
        <Box>
          <Text fontWeight="bold">Who gets the Live Activity emails</Text>
          <Text fontSize="sm">
            The morning summary says whether Memory Issues, DB Health Check and Code Review are good, have minor
            issues, or need immediate attention. It arrives at each person's own hour, in their own time zone. Urgent
            alerts go out straight away: when the database goes down or gets worse, when a server nears its memory
            limit, or when the nightly AI review finds a serious problem.
          </Text>
        </Box>
      </Alert>

      <HStack justify="flex-end">
        <Button size="sm" variant="outline" onClick={reload} isLoading={loading && Boolean(data)}
                data-testid="alerts-refresh">
          Refresh
        </Button>
      </HStack>

      {error && (
        <Alert status="error" borderRadius="md" data-testid="alerts-error">
          <AlertIcon />Couldn't load the alert settings: {error}
        </Alert>
      )}
      {loading && !data && <Spinner />}

      {data?.missing && (
        <Alert status="warning" borderRadius="md" data-testid="alerts-missing">
          <AlertIcon />
          <Box>
            <Text fontWeight="bold">The alert tables aren't in this database yet</Text>
            <Text fontSize="sm">{data.missing}</Text>
          </Box>
        </Alert>
      )}

      {data && !data.missing && (
        <Box borderWidth="1px" borderRadius="md" p={3} data-testid="alerts-recipients">
          <Text fontWeight="bold" mb={2}>People on the list ({recipients.length})</Text>
          {recipients.length > 0 ? (
            <Box overflowX="auto">
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th px={2}>Email</Th><Th px={2}>Time zone</Th><Th px={2}>Morning at</Th>
                    <Th px={2}>Morning summary</Th><Th px={2}>Urgent alerts</Th><Th px={2} />
                  </Tr>
                </Thead>
                <Tbody>
                  {recipients.map((r) => (
                    <RecipientRow key={r.id || r.email} recipient={r} zones={zones} save={save} remove={remove}
                                  onChanged={reload} />
                  ))}
                </Tbody>
              </Table>
            </Box>
          ) : (
            <Text fontSize="sm" color="gray.600">Nobody yet: add someone below.</Text>
          )}
          <AddRecipient zones={zones} existing={existing} save={save} onAdded={reload} />
        </Box>
      )}

      {data && !data.missing && <TestEmail sendTest={sendTest} loadMe={loadMe} />}
      {data && <StatusBox data={data} />}
      {data && !data.missing && <RecentEmails recent={data.recent || []} />}
    </VStack>
  );
}
